import { useCallback, useEffect, useRef, useState } from 'react';

import { getSelectionMarkdown } from '@/utils/textSelection';

interface TextSelectionState {
  text: string;
  markdown: string;
  range: Range | null;
  rect: DOMRect | null;
  elem: HTMLElement | null;
}

interface UseTextSelectionOptions {
  onSelectionChange?: (selection: TextSelectionState) => void;
  allowSelector?: string;
  denySelector?: string;
}

const EMPTY_SELECTION: TextSelectionState = {
  text: '',
  markdown: '',
  range: null,
  rect: null,
  elem: null,
};

const SELECTION_CHANGE_DELAY = 100;

// Helper to get element from node
const getElementFromNode = (node: Node): Element | null => {
  return node.nodeType === Node.ELEMENT_NODE
    ? (node as Element)
    : node.parentElement;
};

export function useTextSelection(options: UseTextSelectionOptions = {}) {
  const {
    allowSelector = '[data-allow-selection]',
    denySelector = '[data-deny-selection]',
  } = options;

  // Use refs to avoid recreating callbacks
  const allowSelectorRef = useRef(allowSelector);
  const denySelectorRef = useRef(denySelector);
  const onSelectionChangeRef = useRef(options.onSelectionChange);

  useEffect(() => {
    allowSelectorRef.current = allowSelector;
    denySelectorRef.current = denySelector;
    onSelectionChangeRef.current = options.onSelectionChange;
  });
  const [selection, setSelection] =
    useState<TextSelectionState>(EMPTY_SELECTION);
  const [isSelecting, setIsSelecting] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Helper to clear pending timeout
  const clearPendingTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const handleSelectionChange = useCallback(() => {
    const windowSelection = window.getSelection();

    if (!windowSelection || windowSelection?.rangeCount === 0) {
      setSelection(EMPTY_SELECTION);
      return;
    }

    const range = windowSelection.getRangeAt(0);
    const selectedText = windowSelection.toString().trim();

    if (selectedText.length === 0) {
      setSelection(EMPTY_SELECTION);
      return;
    }

    // Check if selection is within an element with data-allow-selection
    let elem: HTMLElement | null = null;
    const selector = allowSelectorRef.current;
    const deny = denySelectorRef.current;

    if (selector) {
      const startEl = getElementFromNode(range.startContainer);
      const endEl = getElementFromNode(range.endContainer);

      const startAllowed = startEl?.closest?.(selector);
      const endAllowed = endEl?.closest?.(selector);

      if (!startAllowed || !endAllowed || startAllowed !== endAllowed) {
        setSelection(EMPTY_SELECTION);
        return;
      }

      // A deny ancestor nested inside the allow region opts that subtree out
      // (e.g. an editable markdown editor inside an assistant chat message).
      if (deny) {
        const startDenied = startEl?.closest?.(deny);
        const endDenied = endEl?.closest?.(deny);
        if (
          (startDenied && startAllowed.contains(startDenied)) ||
          (endDenied && endAllowed.contains(endDenied))
        ) {
          setSelection(EMPTY_SELECTION);
          return;
        }
      }

      elem = startAllowed as HTMLElement;
    } else {
      // If no allowSelector, use the common ancestor
      const container = range.commonAncestorContainer;
      elem = getElementFromNode(container) as HTMLElement;
    }

    const selectedMarkdown = getSelectionMarkdown(range, selectedText);
    const rect = range.getBoundingClientRect();

    const newSelection: TextSelectionState = {
      text: selectedText,
      markdown: selectedMarkdown,
      range: range.cloneRange(),
      rect,
      elem,
    };

    setSelection(newSelection);
    onSelectionChangeRef.current?.(newSelection);
  }, []);

  const clearSelection = useCallback(() => {
    window.getSelection()?.removeAllRanges();
    setSelection(EMPTY_SELECTION);
    setIsSelecting(false);
  }, []);

  useEffect(() => {
    const scheduleUpdate = () => {
      clearPendingTimeout();
      timeoutRef.current = setTimeout(
        handleSelectionChange,
        SELECTION_CHANGE_DELAY
      );
    };

    const handleMouseDown = () => {
      setIsSelecting(true);
    };

    const handleMouseUp = () => {
      setIsSelecting(false);
      scheduleUpdate();
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      // Handle keyboard selection changes
      if (
        e.shiftKey ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown'
      ) {
        scheduleUpdate();
      }
    };

    const handleSelectionChangeEvent = () => {
      scheduleUpdate();
    };

    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keyup', handleKeyUp);
    document.addEventListener('selectionchange', handleSelectionChangeEvent);

    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keyup', handleKeyUp);
      document.removeEventListener(
        'selectionchange',
        handleSelectionChangeEvent
      );
      clearPendingTimeout();
    };
  }, [handleSelectionChange, clearPendingTimeout]);

  return {
    selection,
    isSelecting,
    clearSelection,
  };
}
